import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { keyframes } from '@emotion/react'
import { isEqual } from 'lodash'
import * as React from 'react'
import styled from '@emotion/styled/macro'

import type { Product } from '~/types/Product'
import type { Design } from '~/types/Design'
import type { Color } from '~/types/Color'
import type { Customization } from '~/types/Customization'
import {
  useMyDesignListForProduct,
  type MyDesignEntry
} from '~/hooks/useMyDesignListForProduct'
import { fromSavedDesign, fromRequestPayload } from '~/utils/customization'
import { PreviewRenderer } from '~/components/organisms/PreviewRenderer'
import type { View } from '~/types/Design'
import { useLocalMyDesigns } from '~/hooks/useLocalMyDesigns'
import { Dialog, useDialogState } from '~/components/atoms/Dialog'
import { CartButton } from '~/components/atoms/CartButton'
import { LocalStorage } from '~/types/LocalStorage'

interface MyDesignListPanelProps {
  product: Product
  design: Design
  colors: readonly Color[]
  currentCustomization: Customization
  onApply(customization: Customization): void
  onAddNew(): void
  onAddToCart(): void
  isReady?: boolean
}

export const MyDesignListPanel = ({
  product,
  design,
  colors,
  currentCustomization,
  onApply,
  onAddNew,
  onAddToCart,
  isReady
}: MyDesignListPanelProps) => {
  const { list } = useMyDesignListForProduct(product)
  const local = useLocalMyDesigns()
  const [entries, setEntries] = useState<MyDesignEntry[]>([])
  const prevIdsRef = useRef<Set<string>>(new Set())
  const [newlyAddedIds, setNewlyAddedIds] = useState<Set<string>>(new Set())
  const [pendingNewIds, setPendingNewIds] = useState<Set<string>>(new Set())

  // ダイアログをスキップするユーザ設定
  const [skipApplyConfirm, setSkipApplyConfirm] = useState<boolean>(() =>
    typeof window !== 'undefined'
      ? localStorage.getItem(LocalStorage.DoNotShowApplyMyDesignDialog) === '1'
      : false
  )
  const [skipDeleteConfirm, setSkipDeleteConfirm] = useState<boolean>(() =>
    typeof window !== 'undefined'
      ? localStorage.getItem(LocalStorage.DoNotShowDeleteMyDesignDialog) === '1'
      : false
  )

  const refresh = useCallback(async () => {
    const res = await list()

    const toKey = (e: MyDesignEntry) => `${e.origin}-${e.data.id}`
    const nextIds = new Set(res.map((e) => toKey(e)))
    const prevIds = prevIdsRef.current
    // Announced keys buffer (survives across tab switch/render)
    const w = window as unknown as {
      myDesignsJustAdded?: Set<string> | string[]
    }
    const bufSet: Set<string> =
      w.myDesignsJustAdded instanceof Set
        ? w.myDesignsJustAdded
        : new Set(
            Array.isArray(w.myDesignsJustAdded) ? w.myDesignsJustAdded : []
          )
    const announced = Array.from(bufSet)
    const diffAdded = Array.from(nextIds).filter((id) => !prevIds.has(id))
    const isFirstRender = prevIds.size === 0
    const added = isFirstRender
      ? announced.filter((id) => nextIds.has(id))
      : Array.from(new Set([...diffAdded, ...announced]))
    if (added.length > 0) {
      const delayMs = 120
      const visibleMs = 700
      // Immediately mark as pending (opacity: 0)
      setPendingNewIds((prev) => {
        const next = new Set(prev)
        for (const id of added) {
          next.add(id)
        }
        return next
      })
      // After delay, switch to animated state
      window.setTimeout(() => {
        // remove from pending
        setPendingNewIds((prev) => {
          const next = new Set(prev)
          for (const id of added) {
            next.delete(id)
          }
          return next
        })
        setNewlyAddedIds((prev) => {
          const next = new Set(prev)
          for (const id of added) {
            next.add(id)
          }
          return next
        })
        // clear buffer once consumed
        try {
          w.myDesignsJustAdded = new Set<string>()
        } catch {
          /* noop */
        }
      }, delayMs)
      window.setTimeout(() => {
        setNewlyAddedIds((prev) => {
          const next = new Set(prev)
          for (const id of added) {
            next.delete(id)
          }
          return next
        })
      }, delayMs + visibleMs)
    }
    prevIdsRef.current = nextIds

    setEntries(res)
  }, [list])

  useEffect(() => {
    refresh()
  }, [refresh])

  // リアルタイム更新（ローカル保存の更新イベントを購読）
  useEffect(() => {
    const handler = () => {
      refresh()
    }
    window.addEventListener('my-designs:updated', handler)
    return () => window.removeEventListener('my-designs:updated', handler)
  }, [refresh])

  // 追加直後通知で直ちにリスト反映（タブ切替と同時でも演出を実行）
  useEffect(() => {
    const handler = () => {
      refresh()
    }
    window.addEventListener('my-designs:added', handler)
    return () => window.removeEventListener('my-designs:added', handler)
  }, [refresh])

  // 適用確認ダイアログ
  const [confirmDialogProps, confirmDialog] = useDialogState()
  const [pendingCustomization, setPendingCustomization] =
    useState<Customization | null>(null)
  const [applyDontAskChecked, setApplyDontAskChecked] = useState(false)

  // 削除確認ダイアログ
  const [deleteDialogProps, deleteDialog] = useDialogState()
  const [pendingDeleteEntry, setPendingDeleteEntry] =
    useState<MyDesignEntry | null>(null)
  const [deleteDontAskChecked, setDeleteDontAskChecked] = useState(false)

  const handleApply = useCallback(
    (entry: MyDesignEntry) => {
      const [nextCustomization] =
        entry.origin === 'server'
          ? fromSavedDesign(entry.data, { design, colors })
          : fromRequestPayload(entry.data.request, { design, colors })

      if (isEqual(nextCustomization, currentCustomization)) {
        onApply(nextCustomization)
        return
      }

      if (skipApplyConfirm) {
        onApply(nextCustomization)
        return
      }

      setPendingCustomization(nextCustomization)
      confirmDialog.open()
    },
    [
      colors,
      design,
      currentCustomization,
      onApply,
      confirmDialog,
      skipApplyConfirm
    ]
  )

  const handleDelete = useCallback(
    (entry: MyDesignEntry) => {
      if (skipDeleteConfirm) {
        if (entry.origin === 'local') {
          void local.remove(entry.data.id)
        }
        return
      }

      setPendingDeleteEntry(entry)
      deleteDialog.open()
    },
    [deleteDialog, skipDeleteConfirm, local]
  )

  const defaultView = useMemo<View>(() => {
    return design.views[0]
  }, [design.views])

  // （ダイアログの state は上で宣言済み）

  return (
    <Container>
      <List>
        <AddItem
          type="button"
          onClick={onAddNew}
          aria-label="マイデザインを保存"
          title="マイデザインを保存"
        >
          <AddIcon>+</AddIcon>
        </AddItem>
        {entries.map((e) => {
          const itemKey = `${e.origin}-${e.data.id}`
          const isNew = newlyAddedIds.has(itemKey)
          const isPending = pendingNewIds.has(itemKey)
          return (
            <Item
              key={itemKey}
              data-new={isNew || undefined}
              data-new-pending={isPending || undefined}
              onClick={() => handleApply(e)}
            >
              <MiniPreview>
                <StyledPreviewRenderer
                  colors={colors}
                  design={design}
                  view={defaultView}
                  customization={
                    e.origin === 'server'
                      ? fromSavedDesign(e.data, { design, colors })[0]
                      : fromRequestPayload(e.data.request, {
                          design,
                          colors
                        })[0]
                  }
                />
              </MiniPreview>
              {e.origin === 'local' && (
                <DeleteButton
                  type="button"
                  onClick={(ev) => {
                    ev.stopPropagation()
                    handleDelete(e)
                  }}
                  aria-label="削除"
                  title="削除"
                >
                  ×
                </DeleteButton>
              )}
            </Item>
          )
        })}
      </List>
      <PanelFooter>
        <CartButton
          onClick={onAddToCart}
          aria-disabled={!isReady ? 'true' : undefined}
        />
      </PanelFooter>
      <Dialog
        {...confirmDialogProps}
        title="マイデザインを適用"
        style={{ width: 'min(480px, calc(100vw - var(--gutter-medium) * 2))' }}
      >
        <ConfirmBody>
          <p>
            現在のカスタマイズ内容が上書きされます。適用してよろしいですか？
          </p>
          <ConfirmOption>
            <label>
              <input
                type="checkbox"
                checked={applyDontAskChecked}
                onChange={(e) => setApplyDontAskChecked(e.target.checked)}
              />{' '}
              次回からこのダイアログを表示しない
            </label>
          </ConfirmOption>
        </ConfirmBody>
        <ConfirmActions>
          <ConfirmButton
            type="button"
            onClick={() => {
              confirmDialog.close()
              setPendingCustomization(null)
              setApplyDontAskChecked(false)
            }}
          >
            キャンセル
          </ConfirmButton>
          <PrimaryButton
            type="button"
            onClick={() => {
              if (pendingCustomization) {
                onApply(pendingCustomization)
              }
              if (applyDontAskChecked) {
                try {
                  localStorage.setItem(
                    LocalStorage.DoNotShowApplyMyDesignDialog,
                    '1'
                  )
                  setSkipApplyConfirm(true)
                } catch {
                  /* noop */
                }
              }
              confirmDialog.close()
              setPendingCustomization(null)
              setApplyDontAskChecked(false)
            }}
          >
            適用する
          </PrimaryButton>
        </ConfirmActions>
      </Dialog>
      <Dialog
        {...deleteDialogProps}
        title="削除確認"
        style={{ width: 'min(480px, calc(100vw - var(--gutter-medium) * 2))' }}
      >
        <ConfirmBody>
          <p>このマイデザインを削除してよろしいですか？</p>
          <ConfirmOption>
            <label>
              <input
                type="checkbox"
                checked={deleteDontAskChecked}
                onChange={(e) => setDeleteDontAskChecked(e.target.checked)}
              />{' '}
              次回からこのダイアログを表示しない
            </label>
          </ConfirmOption>
        </ConfirmBody>
        <ConfirmActions>
          <ConfirmButton
            type="button"
            onClick={() => {
              deleteDialog.close()
              setPendingDeleteEntry(null)
              setDeleteDontAskChecked(false)
            }}
          >
            キャンセル
          </ConfirmButton>
          <PrimaryButton
            type="button"
            onClick={async () => {
              if (pendingDeleteEntry && pendingDeleteEntry.origin === 'local') {
                await local.remove(pendingDeleteEntry.data.id)
              }
              if (deleteDontAskChecked) {
                try {
                  localStorage.setItem(
                    LocalStorage.DoNotShowDeleteMyDesignDialog,
                    '1'
                  )
                  setSkipDeleteConfirm(true)
                } catch {
                  /* noop */
                }
              }
              // サーバ側削除は未実装
              deleteDialog.close()
              setPendingDeleteEntry(null)
              setDeleteDontAskChecked(false)
            }}
          >
            削除する
          </PrimaryButton>
        </ConfirmActions>
      </Dialog>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const List = styled.div`
  overflow: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: var(--gutter-small);
  padding: var(--gutter-medium);
`

const PanelFooter = styled.div`
  position: sticky;
  bottom: 0;
  padding: var(--gutter-medium);
  border-top: 1px solid var(--gray-300);
  background: var(--white);
  z-index: 1;
  margin-top: auto;
`

const AddItem = styled.button`
  position: relative;
  aspect-ratio: 1 / 1;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  background: var(--gray-50);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: var(--gray-100);
  }

  &:focus-visible {
    outline: 2px solid var(--teal-300);
    outline-offset: 2px;
  }
`

const AddIcon = styled.span`
  font-size: 32px;
  color: var(--gray-600);
  line-height: 1;
`

const itemAppear = keyframes`
  0% { transform: scale(0.92); opacity: 0; }
  60% { transform: scale(1.06); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`

const Item = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  background: var(--gray-0);
  overflow: hidden;
  cursor: pointer;

  &[data-new-pending] {
    opacity: 0;
  }

  &[data-new] {
    animation-name: ${itemAppear};
    animation-duration: 480ms;
    animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1.275);
    animation-delay: 0s;
    animation-iteration-count: 1;
    animation-fill-mode: both;
    /* Prominent border without layout shift (outer rings) */
    box-shadow: 0 0 0 2px var(--teal-400), 0 0 0 4px var(--teal-100);
  }
`

const MiniPreview = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  overflow: hidden;
`

const StyledPreviewRenderer = styled(PreviewRenderer)`
  width: 100%;
  height: 100%;
`

const DeleteButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--gray-300);
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ConfirmBody = styled.div`
  padding: var(--gutter-medium);
  display: flex;
  flex-direction: column;
  gap: var(--gutter-medium);
  font-size: 14px;
  color: var(--text-main);
`

const ConfirmActions = styled.div`
  display: flex;
  gap: var(--gutter-small);
  justify-content: flex-end;
  padding: var(--gutter-medium);
  border-top: 1px solid var(--gray-300);
`

const ConfirmOption = styled.div`
  font-size: 14px;
  color: var(--text-main);
  text-align: center;
  padding: var(--gutter-small);
`

const ConfirmButton = styled.button`
  padding: 8px 12px;
  font-size: 13px;
`

const PrimaryButton = styled(ConfirmButton)`
  background: var(--black);
  color: #fff;
  border-radius: 4px;
`
